import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from "react";
import { Bot, ChevronRight, Loader2, Send, Sparkles, X } from "lucide-react";
import { AssistantMarkdown } from "@/components/chat/AssistantMarkdown";
import { Button } from "@/components/ui-ext/primitives";
import { type CosmosQueryResponse, useCosmosClient } from "@/lib/cosmos-api";
import { cn } from "@/lib/utils";

export interface InsightsChatSidebarHandle {
	ask: (prompt: string) => void;
}

interface ChatMessage {
	id: string;
	role: "user" | "assistant";
	content: string;
	executionSnapshot?: CosmosQueryResponse["executionSnapshot"];
	error?: boolean;
}

const STORAGE_KEY = "ei-chat-open";

const newId = (): string =>
	globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const initialOpenState = (): boolean => {
	if (typeof window === "undefined") {
		return false;
	}
	return window.localStorage.getItem(STORAGE_KEY) === "true";
};

const buildConversationHistory = (messages: ChatMessage[]) =>
	messages
		.filter((message) => message.content.trim())
		.map((message) => ({
			role: message.role,
			content: message.content,
			executionSnapshot: message.executionSnapshot
		}));

export const InsightsChatSidebar = forwardRef<InsightsChatSidebarHandle>((_props, ref) => {
	const cosmos = useCosmosClient();
	const [open, setOpenState] = useState(initialOpenState);
	const [input, setInput] = useState("");
	const [messages, setMessages] = useState<ChatMessage[]>([]);
	const [sending, setSending] = useState(false);
	const sendingRef = useRef(false);

	const setOpen = useCallback((next: boolean) => {
		setOpenState(next);
		window.localStorage.setItem(STORAGE_KEY, String(next));
	}, []);

	const submit = useCallback(
		(prompt: string) => {
			const trimmed = prompt.trim();
			if (!trimmed || sendingRef.current) {
				return;
			}
			setOpen(true);
			setInput("");
			sendingRef.current = true;
			setSending(true);
			const userMessage: ChatMessage = {
				id: newId(),
				role: "user",
				content: trimmed
			};
			const assistantMessageId = newId();
			const assistantMessage: ChatMessage = {
				id: assistantMessageId,
				role: "assistant",
				content: ""
			};
			const history = buildConversationHistory(messages);
			setMessages((current) => [...current, userMessage, assistantMessage]);
			void cosmos.streamQuery(
				trimmed,
				{
					onDelta: (chunk) => {
						setMessages((current) =>
							current.map((message) =>
								message.id === assistantMessageId
									? { ...message, content: message.content + chunk }
									: message
							)
						);
					},
					onDone: (response) => {
						setMessages((current) =>
							current.map((message) =>
								message.id === assistantMessageId
									? {
											...message,
											content: message.content || response.assistantResponse?.text || "",
											executionSnapshot: response.executionSnapshot
										}
									: message
							)
						);
					},
					onError: (error) => {
						setMessages((current) =>
							current.map((message) =>
								message.id === assistantMessageId
									? {
											...message,
											content: error.message,
											error: true
										}
									: message
							)
						);
					}
				},
				{ conversationHistory: history, engineeringInsightsMode: true }
			).finally(() => {
				sendingRef.current = false;
				setSending(false);
			});
		},
		[cosmos, messages, setOpen]
	);

	useImperativeHandle(ref, () => ({ ask: submit }), [submit]);

	const hasMessages = messages.length > 0;
	const body = useMemo(
		() =>
			hasMessages ? (
				<div className="flex flex-col gap-3">
					{messages.map((message) => (
						<div
							key={message.id}
							className={cn(
								"rounded-[12px] px-3 py-2 text-sm leading-relaxed",
								message.role === "user"
									? "ml-8 bg-[var(--primary)] text-[color:var(--primary-foreground)]"
									: "mr-8 bg-muted text-anchor",
								message.error && "bg-[var(--danger)]/10 text-[var(--danger)]"
							)}
						>
							{message.role === "assistant" ? (
								message.content ? (
									<AssistantMarkdown text={message.content} />
								) : (
									<div className="inline-flex items-center gap-2 text-muted-foreground">
										<Loader2 className="h-3.5 w-3.5 animate-spin" />
										Thinking...
									</div>
								)
							) : (
								message.content
							)}
						</div>
					))}
				</div>
			) : (
				<div className="grid h-full place-items-center px-4 text-center text-sm text-muted-foreground">
					<div>
						<div className="mx-auto mb-3 grid h-10 w-10 place-items-center rounded-xl bg-[var(--primary)]/10">
							<Bot className="h-5 w-5 text-[var(--primary)]" />
						</div>
						Ask about this dashboard or use Explore on a chart to investigate it here.
					</div>
				</div>
			),
		[hasMessages, messages]
	);

	return (
		<>
			{/* Bottom-anchored so it stays clear of the presentation overlay's
			    mid-right next-slide arrow (this button floats above the deck). */}
			<button
				type="button"
				onClick={() => setOpen(!open)}
				className={cn(
					"fixed bottom-6 right-3 z-40 hidden rounded-full border border-soft bg-surface p-2 shadow-card ring-focus lg:inline-flex",
					open && "right-[396px]"
				)}
				aria-label={open ? "Collapse insights chat" : "Expand insights chat"}
				title="Ask about this data"
			>
				{open ? (
					<ChevronRight className="h-4 w-4" />
				) : (
					<Sparkles className="h-4 w-4 text-[var(--primary)]" />
				)}
			</button>
			<aside
				className={cn(
					"fixed inset-y-0 right-0 z-50 flex w-full max-w-[390px] flex-col border-l border-soft bg-surface shadow-card transition-transform duration-200 lg:sticky lg:top-0 lg:h-screen lg:shrink-0",
					open ? "translate-x-0" : "translate-x-full lg:hidden"
				)}
				aria-label="Engineering insights chat"
			>
				<div className="flex items-center justify-between border-b border-soft px-4 py-3">
					<div>
						<div className="text-sm font-semibold text-anchor">Ask about this data</div>
						<div className="text-xs text-muted-foreground">Engineering insights chat</div>
					</div>
					<Button variant="ghost" size="sm" onClick={() => setOpen(false)} aria-label="Close chat">
						<X className="h-4 w-4" />
					</Button>
				</div>
				<div className="min-h-0 flex-1 overflow-y-auto p-4">{body}</div>
				<form
					className="border-t border-soft p-3"
					onSubmit={(event) => {
						event.preventDefault();
						submit(input);
					}}
				>
					<div className="flex items-end gap-2 rounded-[12px] border border-soft bg-background p-2 focus-within:border-[var(--primary)]">
						<textarea
							value={input}
							onChange={(event) => setInput(event.target.value)}
							onKeyDown={(event) => {
								if (event.key === "Enter" && !event.shiftKey) {
									event.preventDefault();
									submit(input);
								}
							}}
							rows={2}
							placeholder="Why did cycle time spike?"
							className="max-h-32 min-h-10 flex-1 resize-none bg-transparent text-sm text-anchor outline-none placeholder:text-muted-foreground"
						/>
						<Button size="sm" disabled={!input.trim() || sending} aria-label="Send message">
							{sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
						</Button>
					</div>
				</form>
			</aside>
		</>
	);
});

InsightsChatSidebar.displayName = "InsightsChatSidebar";

